import type { ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { toaster } from 'components/v2/toast';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { resolveDigitalVersion } from 'services/api/dynamicFormsApi';
import { PcrSelection } from '../store/createDocumentStore';
import { useDocumentStore } from './useDocumentStore';

export type DigitalVersionSwitchRequest = {
  selection: PcrSelection;
  // The dialog shown when the selection moves the document to another schema; the caller owns the
  // wording because only it knows what was selected (PCR or c-PCR, and the option label).
  confirmation: { caption: string; description: ReactNode };
  // Called when the selection is not saved because the lookup failed or the dialog was cancelled; lets a caller
  // undo an edit that started the switch.
  onCancel?: () => void;
};

// One flow for PCR and c-PCR changes: look up the digital version of the selection, save it directly when the
// schema stays the same, otherwise confirm, save and reload the form. A failed lookup only shows an error - nothing
// was changed, so there is nothing to reload. A rejected save shows an error and reloads the saved form.
export function useDigitalVersionSwitch() {
  const { docId, getSchemaId, setDigitalVersion, reloadData } = useDocumentStore();

  const showError = (error: unknown) => {
    let details = 'An error occurred while performing the operation. Please try again.';
    if (isAxiosError(error) && error.response?.data?.message) {
      details = error.response.data.message;
    }
    console.error('Error during digital version switch', error);
    toaster({ severity: 'error', summary: 'Operation failed', details });
  };

  const save = async (selection: PcrSelection, schemaChanges: boolean) => {
    try {
      await setDigitalVersion(selection, schemaChanges);
    } catch (error) {
      showError(error);
      reloadData();
    }
  };

  const switchDigitalVersion = async ({ selection, confirmation, onCancel }: DigitalVersionSwitchRequest): Promise<void> => {
    let nextSchemaId: string;
    try {
      nextSchemaId = (await resolveDigitalVersion(docId, selection.pcrId, selection.cpcrId)).code;
    } catch (error) {
      showError(error);
      onCancel?.();
      return;
    }

    if (nextSchemaId === getSchemaId()) {
      await save(selection, false);
      return;
    }

    confirmAlert({
      caption: confirmation.caption,
      description: confirmation.description,
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      onConfirm: () => save(selection, true),
      onReject: onCancel,
    });
  };

  return { switchDigitalVersion };
}
